import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Order } from '../../../common/model/dto/order/order';
import { OrderDataUtils } from '../../../common/utils/services/order-data-utils';
import StringUtils from '../../../common/utils/services/string-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import OvLabelledField from '../atoms/OvLabelledField';
import OvOrderEditButton from '../atoms/OvOrderEditButton';
import OvCopyToClipboardButton from '../molecules/OvCopyToClipboardButton';
import OvRecordDetailsSection from '../molecules/OvRecordDetailsSection';

interface OvOrderInfoSectionProps {
  order: Order;
  isUpdateAllowed: boolean;
  isEditDisabled: boolean;
  onEdit: () => void;
}

const OvOrderInfoSection: FC<OvOrderInfoSectionProps> = ({
  order,
  isUpdateAllowed,
  isEditDisabled,
  onEdit,
}) => {
  const { t } = useTranslation();
  const stripePaymentUrl = OrderDataUtils.getStripePaymentUrl(order);
  const shopifyOrderUrl = OrderDataUtils.getShopifyOrderUrl(order);

  return (
    <OvRecordDetailsSection
      title={t('orders.details.orderDetails')}
      action={
        isUpdateAllowed && (
          <OvOrderEditButton disabled={isEditDisabled} onClick={onEdit} />
        )
      }
    >
      <Pairs>
        <OvLabelledField label={t('orders.generalInfo.channel')}>
          {StringUtils.displayValue(order.channel)}
        </OvLabelledField>
        <OvLabelledField label={t('orders.generalInfo.salesChannel')}>
          {StringUtils.displayValue(order.sales_channel)}
        </OvLabelledField>
        <OvLabelledField label={t('orders.generalInfo.discountCode')}>
          {StringUtils.displayValue(order.discount_code)}
        </OvLabelledField>
        <OvLabelledField label={t('orders.generalInfo.isSubscription')}>
          {StringUtils.getPrettifiedBooleanValue(order.is_subscription)}
        </OvLabelledField>
      </Pairs>
      <Notes>
        <h3>{t('orders.generalInfo.notes')}</h3>
        <p>{order.note?.trim() || t('orders.details.noNotes')}</p>
      </Notes>
      <AllFields>
        <summary>{t('orders.details.allOrderFields')}</summary>
        <Pairs>
          <OvLabelledField label={t('orders.generalInfo.internalId')}>
            {StringUtils.displayValue(order.internal_id)}
            {order.internal_id && (
              <OvCopyToClipboardButton
                text={order.internal_id}
                label={t('orders.details.copyInternalId')}
              />
            )}
          </OvLabelledField>
          <OvLabelledField label={t('orders.generalInfo.createdAt')}>
            {OrderDataUtils.formatDateTime(order.created_at)}
          </OvLabelledField>
          <OvLabelledField label={t('common.orderedAt')}>
            {OrderDataUtils.formatDateTime(order.ordered_at)}
          </OvLabelledField>
          <OvLabelledField
            label={t('orders.generalInfo.isStripeSubscriptionFirstOrder')}
          >
            {StringUtils.getPrettifiedBooleanValue(
              order.is_stripe_subscription_first_order
            )}
          </OvLabelledField>
          <OvLabelledField label={t('orders.generalInfo.stripeInvoiceNumber')}>
            {StringUtils.displayValue(order.stripe_invoice_number)}
          </OvLabelledField>
          <OvLabelledField label={t('orders.generalInfo.isPromoOrder')}>
            {StringUtils.getPrettifiedBooleanValue(order.is_promo_order)}
          </OvLabelledField>
        </Pairs>
      </AllFields>
      {(stripePaymentUrl || shopifyOrderUrl) && (
        <Links>
          {stripePaymentUrl && (
            <a
              href={stripePaymentUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('orders.stripePaymentLink')}
            </a>
          )}
          {shopifyOrderUrl && (
            <a href={shopifyOrderUrl} target="_blank" rel="noopener noreferrer">
              {t('common.shopifyLink')}
            </a>
          )}
        </Links>
      )}
    </OvRecordDetailsSection>
  );
};

export default OvOrderInfoSection;

const Pairs = styled.dl`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem 1.25rem;
  margin: 0;
`;

const Notes = styled.div`
  margin-top: 0.875rem;

  h3 {
    margin: 0;
    color: ${Colours.OV_CHARCOAL};
    font-size: ${Variables.fontSizes.SMALL};
    font-weight: normal;
  }

  p {
    margin: 0.1875rem 0 0;
    white-space: pre-wrap;
    overflow-wrap: break-word;
  }
`;

const AllFields = styled.details`
  margin-top: 0.75rem;
  padding-top: 0.625rem;
  border-top: 1px solid rgb(235, 237, 240);

  summary {
    font-size: ${Variables.fontSizes.SMALL};
    cursor: pointer;
  }

  summary:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }

  &[open] summary {
    margin-bottom: 0.75rem;
  }
`;

const Links = styled.p`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  margin: 0.75rem 0 0;
`;
